/**
 * Arbitrary-precision decimal arithmetic.
 *
 * @remarks
 * Avoids floating-point drift by representing values as `coefficient * 10^(-scale)`
 * using bigint. For example, 12.34 is `{ coefficient: 1234n, scale: 2 }`.
 *
 * All values are normalized - trailing fractional zeros are stripped and
 * zero is always `{ 0n, 0 }`.
 */
export type Decimal = {
  coefficient: bigint
  scale: number
}

const DEFAULT_DIVISION_SCALE = 28
const POW10: bigint[] = [1n]

const ZERO = Object.freeze({
  coefficient: 0n,
  scale: 0
}) as Readonly<Decimal>

function parse(input: string): Decimal | undefined {
  const trimmed = input.trim()
  const match = trimmed.match(/^([+-])?(?:(\d+)(?:\.(\d*))?|\.(\d+))$/)
  if (!match) {
    return undefined
  }

  const integerPart = match[2] ?? '0'
  const fractionPart = match[3] ?? match[4] ?? ''
  const digits = `${integerPart}${fractionPart}`.replace(/^0+(?=\d)/, '')
  const sign = match[1] == '-' ? -1n : 1n

  return normalize({
    coefficient: sign * BigInt(digits == '' ? '0' : digits),
    scale: fractionPart.length
  })
}

function add(left: Decimal, right: Decimal): Decimal {
  const scale = Math.max(left.scale, right.scale)

  return normalize({
    coefficient: scaleCoefficient(left, scale) + scaleCoefficient(right, scale),
    scale
  })
}

function subtract(left: Decimal, right: Decimal): Decimal {
  const scale = Math.max(left.scale, right.scale)

  return normalize({
    coefficient: scaleCoefficient(left, scale) - scaleCoefficient(right, scale),
    scale
  })
}

function multiply(left: Decimal, right: Decimal): Decimal {
  const scale = left.scale + right.scale
  return normalize({
    coefficient: left.coefficient * right.coefficient,
    scale
  })
}

function divide(left: Decimal, right: Decimal): Decimal | undefined {
  if (right.coefficient == 0n) {
    return undefined
  }

  const numerator = left.coefficient * pow10(right.scale + DEFAULT_DIVISION_SCALE)
  const denominator = right.coefficient * pow10(left.scale)

  return normalize({
    coefficient: roundQuotient(numerator, denominator),
    scale: DEFAULT_DIVISION_SCALE
  })
}

function formatFixed(value: Decimal, scale: number): string {
  const scaled = rescale(value, scale)
  const negative = scaled.coefficient < 0n
  const digits = absBigInt(scaled.coefficient).toString().padStart(scale + 1, '0')
  const integerPart = scale == 0 ? digits : digits.slice(0, -scale)
  const fractionalPart = scale == 0 ? '' : digits.slice(-scale)
  const sign = negative ? '-' : ''

  return scale == 0 ? `${sign}${integerPart}` : `${sign}${integerPart}.${fractionalPart}`
}

function rescale(value: Decimal, scale: number): Decimal {
  if (scale >= value.scale) {
    return {
      coefficient: value.coefficient * pow10(scale - value.scale),
      scale
    }
  }

  const divisor = pow10(value.scale - scale)

  return {
    coefficient: roundQuotient(value.coefficient, divisor),
    scale
  }
}

function scaleCoefficient(value: Decimal, targetScale: number): bigint {
  return value.coefficient * pow10(targetScale - value.scale)
}

function normalize(value: Decimal): Decimal {
  let { coefficient, scale } = value

  if (coefficient == 0n) {
    return ZERO
  }

  while (scale > 0 && coefficient % 10n == 0n) {
    coefficient /= 10n
    scale--
  }

  return { coefficient, scale }
}

function roundQuotient(numerator: bigint, denominator: bigint): bigint {
  const quotient = numerator / denominator
  const remainder = numerator % denominator

  if (remainder == 0n) {
    return quotient
  }

  const absRemainder = absBigInt(remainder)
  const absDenominator = absBigInt(denominator)

  if (absRemainder * 2n < absDenominator) {
    return quotient
  }

  return quotient + signBigInt(numerator) * signBigInt(denominator)
}

function absBigInt(value: bigint): bigint {
  return value < 0n ? -value : value
}

function signBigInt(value: bigint): bigint {
  return value < 0n ? -1n : 1n
}

function pow10(exponent: number): bigint {
  while (POW10.length <= exponent) {
    POW10.push(POW10[POW10.length - 1] * 10n)
  }

  return POW10[exponent]
}

export const Decimal = {
  ZERO,
  parse,
  add,
  subtract,
  multiply,
  divide,
  formatFixed
}
